import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_URL } from "../constants";
import { Loading } from "../components/Loading";
import { NoMatch } from "../components/NoMatch";
import { useBranding } from "./Branding";
import './StaticPage.css';

interface PageItem {
  title: string;
  content: string;
  titleColor?: string | null;
}

export const StaticPage = () => {
  const params = useParams<{ country?: string; language: string; page: string }>();
  const [searchParams] = useSearchParams();
  const { setBranding } = useBranding();

  // ISO alpha-3 country, from /:country/:language/:page or ?country=XXX. Empty means the
  // default page; the backend falls back to it when no country-specific page exists.
  const country = (params.country ?? searchParams.get('country') ?? '').trim().toUpperCase();

  const [loading, setLoading] = useState(true);
  const [pageItem, setPageItem] = useState<PageItem>({
    title: '',
    content: ''
  })

  useEffect(() => {
    const page = params.page ?? "";
    if (page) {
      setLoading(true);
      const language = params.language ?? 'en';
      const query = country ? `?country=${encodeURIComponent(country)}` : '';
      fetch(`${API_URL}/admin/agreement/public/${page}/${language}${query}`).then(async response => {
        const result = await response.json();
        const content = result?.content;
        setPageItem({
          title: content?.title,
          content: content?.content,
          titleColor: content?.titleColor,
        })
        // The header logo lives in the layout, so hand the page's branding up to it.
        setBranding({
          logoUrl: content?.logoUrl,
          titleColor: content?.titleColor,
          footerText: content?.footerText,
          footerColor: content?.footerColor,
          footerTextColor: content?.footerTextColor,
        });
      }).finally(() => {
        setLoading(false);
      })
    }
    // Back to the default branding when leaving the page.
    return () => setBranding({});
  }, [params.page, params.language, country, setBranding]);

  if (loading) {
    return <Loading/>;
  }

  const titleStyle = pageItem.titleColor ? { color: pageItem.titleColor } : undefined;

  return (
    <section className="static-page">
      {pageItem?.title && <h1 style={titleStyle}>{pageItem.title}</h1>}
      {pageItem?.content && <article dangerouslySetInnerHTML={{ __html: pageItem.content }}/>}

      {!pageItem?.title && !pageItem?.content && (
        <NoMatch/>
      )}
    </section>
  )
};
